import React, { useEffect, useState, useMemo, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { AppLayout } from "@/components/AppLayout";
import { ShieldCheck, Lock, Users, ExternalLink, Search, AlertCircle, UserPlus, X } from "lucide-react";
import axios from "axios";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const HouseholdAdminOverview = ({ user }) => {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [forbidden, setForbidden] = useState(false);
  const [q, setQ] = useState("");

  // Manage-admins state
  const [admins, setAdmins] = useState(null);
  const [newAdminEmail, setNewAdminEmail] = useState("");
  const [addingAdmin, setAddingAdmin] = useState(false);
  const [removingEmail, setRemovingEmail] = useState(null);

  const loadAdmins = useCallback(async () => {
    try {
      const r = await axios.get(`${BACKEND_URL}/api/admin/super-admins`, { withCredentials: true });
      setAdmins(r.data);
    } catch {
      // silent — the overview call above handles the 403
    }
  }, []);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const r = await axios.get(`${BACKEND_URL}/api/admin/households-overview`, { withCredentials: true });
        if (alive) setData(r.data);
        await loadAdmins();
      } catch (err) {
        if (err.response?.status === 403) {
          if (alive) setForbidden(true);
        } else {
          toast.error("Failed to load admin overview");
        }
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; };
  }, [loadAdmins]);

  const handleAddAdmin = async (e) => {
    e.preventDefault();
    const email = newAdminEmail.trim().toLowerCase();
    if (!email || !email.includes("@")) {
      toast.error("Enter a valid email");
      return;
    }
    setAddingAdmin(true);
    try {
      await axios.post(`${BACKEND_URL}/api/admin/super-admins`, { email }, { withCredentials: true });
      toast.success(`${email} is now a super admin`);
      setNewAdminEmail("");
      await loadAdmins();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to add admin");
    } finally {
      setAddingAdmin(false);
    }
  };

  const handleRemoveAdmin = async (email) => {
    if (!window.confirm(`Remove super-admin access from ${email}?`)) return;
    setRemovingEmail(email);
    try {
      await axios.delete(`${BACKEND_URL}/api/admin/super-admins/${encodeURIComponent(email)}`, { withCredentials: true });
      toast.success(`${email} is no longer a super admin`);
      await loadAdmins();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to remove admin");
    } finally {
      setRemovingEmail(null);
    }
  };

  const filtered = useMemo(() => {
    const rows = data?.households || [];
    const needle = q.trim().toLowerCase();
    if (!needle) return rows;
    return rows.filter(h =>
      (h.household_name || "").toLowerCase().includes(needle) ||
      (h.owner_email || "").toLowerCase().includes(needle) ||
      (h.owner_name || "").toLowerCase().includes(needle) ||
      (h.team_member_emails || []).some(e => e.toLowerCase().includes(needle))
    );
  }, [data, q]);

  if (forbidden) {
    return (
      <AppLayout user={user}>
        <div className="max-w-md mx-auto mt-16 p-8 bg-white rounded-2xl border border-stone-200 text-center" data-testid="admin-forbidden">
          <AlertCircle className="w-10 h-10 text-amber-500 mx-auto mb-3" />
          <h1 className="text-xl font-heading font-extrabold text-stone-900 mb-2">Restricted</h1>
          <p className="text-sm text-stone-500">This page is only visible to super admins.</p>
        </div>
      </AppLayout>
    );
  }

  return (
    <AppLayout user={user}>
      <div className="space-y-6" data-testid="admin-overview-page">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div>
            <div className="flex items-center gap-2 text-[10px] font-bold uppercase tracking-widest text-emerald-600 mb-1">
              <ShieldCheck className="w-3.5 h-3.5" /> Super Admin
            </div>
            <h1 className="text-2xl sm:text-3xl font-heading font-extrabold text-stone-900">Household Overview</h1>
            <p className="text-sm text-stone-500 mt-1">Every household in the system, with owner, team access and finalized months.</p>
          </div>
          <div className="flex items-center gap-2">
            <div className="text-right">
              <div className="text-[10px] uppercase tracking-widest text-stone-400 font-bold">Total households</div>
              <div className="text-2xl font-heading font-extrabold text-stone-900">{data?.total ?? "—"}</div>
            </div>
          </div>
        </div>

        <div className="relative">
          <Search className="w-4 h-4 text-stone-400 absolute left-3 top-1/2 -translate-y-1/2" />
          <input
            type="text"
            value={q}
            onChange={e => setQ(e.target.value)}
            placeholder="Search by household, owner email, or team member…"
            className="w-full pl-9 pr-4 py-2.5 text-sm rounded-xl border border-stone-200 bg-white focus:outline-none focus:border-[#4F6F52] focus:ring-2 focus:ring-[#4F6F52]/10 transition"
            data-testid="admin-search-input"
          />
        </div>

        {/* Manage super admins */}
        {admins && (
          <div className="bg-white rounded-2xl border border-stone-200 p-5" data-testid="manage-admins-section">
            <div className="flex items-start justify-between gap-4 flex-wrap mb-4">
              <div>
                <h2 className="text-sm font-heading font-extrabold text-stone-900 flex items-center gap-1.5">
                  <ShieldCheck className="w-4 h-4 text-emerald-600" /> Super admins
                </h2>
                <p className="text-xs text-stone-500 mt-0.5">These emails get owner-level access to every household. Additions take effect on their next request — no redeploy.</p>
              </div>
              <form onSubmit={handleAddAdmin} className="flex items-center gap-2 flex-1 min-w-[280px] max-w-md justify-end">
                <input
                  type="email"
                  value={newAdminEmail}
                  onChange={e => setNewAdminEmail(e.target.value)}
                  placeholder="new-admin@example.com"
                  className="flex-1 px-3 py-2 text-sm rounded-lg border border-stone-200 bg-stone-50 focus:outline-none focus:border-[#4F6F52] focus:bg-white transition"
                  data-testid="add-admin-input"
                  disabled={addingAdmin}
                />
                <button
                  type="submit"
                  disabled={addingAdmin || !newAdminEmail.trim()}
                  className="inline-flex items-center gap-1.5 px-3 py-2 text-sm font-medium text-white bg-gradient-to-r from-[#4F6F52] to-[#5a7a5e] hover:from-[#3f5942] hover:to-[#4F6F52] rounded-lg shadow-sm disabled:opacity-50 disabled:cursor-not-allowed transition"
                  data-testid="add-admin-button"
                >
                  <UserPlus className="w-3.5 h-3.5" /> Add
                </button>
              </form>
            </div>

            <div className="flex flex-wrap gap-2">
              {admins.env_admins.map(email => (
                <span key={email} className="inline-flex items-center gap-1.5 px-2.5 py-1.5 text-xs font-medium bg-stone-100 text-stone-700 rounded-full" data-testid={`env-admin-${email}`}>
                  <ShieldCheck className="w-3 h-3 text-emerald-600" />
                  {email}
                  <span className="text-[9px] uppercase tracking-widest text-stone-400 font-bold ml-1">env</span>
                </span>
              ))}
              {admins.db_admins.map(a => (
                <span key={a.email} className="inline-flex items-center gap-1.5 pl-2.5 pr-1.5 py-1 text-xs font-medium bg-emerald-50 text-emerald-800 rounded-full border border-emerald-100" data-testid={`db-admin-${a.email}`}>
                  <ShieldCheck className="w-3 h-3" />
                  {a.email}
                  {a.added_by && <span className="text-[9px] text-emerald-600/70">· by {a.added_by}</span>}
                  <button
                    onClick={() => handleRemoveAdmin(a.email)}
                    disabled={removingEmail === a.email || (user?.email || "").toLowerCase() === a.email}
                    className="ml-1 w-5 h-5 flex items-center justify-center rounded-full hover:bg-red-100 hover:text-red-600 text-emerald-600 disabled:opacity-30 disabled:cursor-not-allowed transition"
                    title={(user?.email || "").toLowerCase() === a.email ? "You cannot remove yourself" : `Remove ${a.email}`}
                    data-testid={`remove-admin-${a.email}`}
                  >
                    <X className="w-3 h-3" />
                  </button>
                </span>
              ))}
              {admins.env_admins.length === 0 && admins.db_admins.length === 0 && (
                <span className="text-xs text-stone-400">No super admins configured.</span>
              )}
            </div>
          </div>
        )}

        {loading ? (
          <div className="py-24 text-center text-sm text-stone-400">Loading…</div>
        ) : filtered.length === 0 ? (
          <div className="py-24 text-center text-sm text-stone-400">No households match your search.</div>
        ) : (
          <div className="bg-white rounded-2xl border border-stone-200 overflow-hidden">
            <div className="hidden md:grid grid-cols-12 gap-2 px-5 py-3 bg-stone-50 border-b border-stone-100 text-[10px] font-bold uppercase tracking-widest text-stone-400">
              <div className="col-span-3">Household</div>
              <div className="col-span-3">Owner</div>
              <div className="col-span-3">Team access</div>
              <div className="col-span-2">Finalized months</div>
              <div className="col-span-1 text-right">Stats</div>
            </div>
            {filtered.map(h => (
              <div key={h.config_id} className="grid grid-cols-1 md:grid-cols-12 gap-2 px-5 py-4 border-b border-stone-100 last:border-b-0 hover:bg-stone-50/50 transition" data-testid={`admin-row-${h.config_id}`}>
                <div className="md:col-span-3">
                  <button
                    onClick={() => navigate(`/households/${h.config_id}`)}
                    className="text-sm font-semibold text-stone-900 hover:text-[#4F6F52] flex items-center gap-1.5 text-left"
                    data-testid={`open-household-${h.config_id}`}
                  >
                    {h.household_name}
                    <ExternalLink className="w-3 h-3 text-stone-400" />
                  </button>
                  {h.bill_splitting_enabled && (
                    <div className="mt-1 inline-flex items-center gap-1 text-[9px] uppercase tracking-widest font-bold text-emerald-600">
                      <Users className="w-2.5 h-2.5" /> Split · {h.members?.length || 0}
                    </div>
                  )}
                </div>
                <div className="md:col-span-3 flex flex-col">
                  <span className="text-sm text-stone-700 truncate">{h.owner_email || "—"}</span>
                  {h.owner_name && <span className="text-xs text-stone-400 truncate">{h.owner_name}</span>}
                </div>
                <div className="md:col-span-3">
                  {(h.team_member_emails || []).length === 0 ? (
                    <span className="text-xs text-stone-400">No team invites</span>
                  ) : (
                    <div className="flex flex-wrap gap-1">
                      {h.team_member_emails.map(email => (
                        <span key={email} className="inline-flex items-center gap-1 px-2 py-0.5 text-[10px] font-medium bg-stone-100 text-stone-600 rounded-full">
                          {email}
                        </span>
                      ))}
                    </div>
                  )}
                </div>
                <div className="md:col-span-2">
                  {(h.closed_months || []).length === 0 ? (
                    <span className="text-xs text-stone-400">None</span>
                  ) : (
                    <div className="flex flex-wrap gap-1">
                      {h.closed_months.map(m => (
                        <span key={m} className="inline-flex items-center gap-1 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider bg-amber-50 border border-amber-200 text-amber-700 rounded-full">
                          <Lock className="w-2.5 h-2.5" /> {m}
                        </span>
                      ))}
                    </div>
                  )}
                </div>
                <div className="md:col-span-1 md:text-right">
                  <div className="text-sm font-semibold text-stone-900">{h.completed_count}</div>
                  <div className="text-[10px] uppercase tracking-widest text-stone-400 font-bold">Txns</div>
                  {h.skipped_closed_count > 0 && (
                    <div className="text-[10px] text-amber-600 font-semibold mt-0.5">+{h.skipped_closed_count} skipped</div>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </AppLayout>
  );
};

export default HouseholdAdminOverview;
